/**
 * "Your sign-in did not complete" notice on the landing page.
 *
 * A load that arrives with `?code=` on the redirect path is AuthKit's sign-in
 * callback. When it ends with no session the landing page renders, and the
 * visitor otherwise sees the plain landing page with no hint that a sign-in
 * just failed. It happens when:
 *   - a sign-in started on an older bundle finishes on a newer one built with
 *     a different client id (the code belongs to the other application), or
 *   - the PKCE verifier authkit-js keeps in sessionStorage is gone (new tab,
 *     storage cleared), so the code cannot be exchanged.
 * authkit-js logs the cause to the console and then strips the query string
 * (`history.replaceState`) whatever the outcome, so by the time the landing
 * page renders `?code=` is already gone. {@link captureAuthCallbackLoad} runs
 * from main.tsx before anything mounts and remembers whether it was there.
 */

export const SIGN_IN_INCOMPLETE_MESSAGE = 'Your sign-in did not complete. Please sign in again.';

/**
 * Same test as authkit-js 0.20 `isRedirectCallback`: a `code` parameter on the
 * redirect URI's path (with or without a trailing slash). `redirectUri`
 * defaults to the origin, as in authkit-js.
 */
export function isAuthCallbackLoad(
  location: { pathname: string; search: string },
  redirectUri: string | undefined,
  origin: string,
): boolean {
  if (!new URLSearchParams(location.search).has('code')) return false;
  let redirectPath: string;
  try {
    redirectPath = new URL(redirectUri || origin).pathname;
  } catch {
    return false;
  }
  return location.pathname === redirectPath || location.pathname === `${redirectPath}/`;
}

let captured: boolean | null = null;

/**
 * Remember, once per page load, whether this load is a sign-in callback. The
 * first call wins; later calls (after authkit-js cleaned the URL) return the
 * remembered answer.
 */
export function captureAuthCallbackLoad(
  location: { pathname: string; search: string } = window.location,
  redirectUri: string | undefined = import.meta.env.VITE_WORKOS_REDIRECT_URI,
  origin: string = window.location.origin,
): boolean {
  if (captured === null) captured = isAuthCallbackLoad(location, redirectUri, origin);
  return captured;
}

/** True when this page load was a sign-in callback (false before capture ran). */
export function loadedWithAuthCode(): boolean {
  return captured === true;
}
